/**
 * Firebase Backend Adapter for Self-Hosted Excalidraw
 * ====================================================
 *
 * This module replaces Firebase SDK imports with an adapter that talks to
 * your own backend server, enabling full collaboration persistence without
 * depending on Google Firebase.
 *
 * REQUIRED BACKEND ENDPOINTS:
 *
 *   GET  /api/scenes/:roomId
 *        Returns: { sceneVersion: number, iv: string (base64), ciphertext: string (base64) }
 *        Returns: 404 if room doesn't exist
 *
 *   PUT  /api/scenes/:roomId
 *        Body: { sceneVersion: number, iv: string (base64), ciphertext: string (base64) }
 *        Returns: { success: true }
 *
 *   GET  /api/files/:prefix/:fileId
 *        Returns: Raw binary file data
 *        Returns: 404 if file doesn't exist
 *
 *   PUT  /api/files/:prefix/:fileId
 *        Body: Raw binary file data
 *        Headers: Cache-Control (optional, passed from client)
 *        Returns: { success: true }
 *
 * ENVIRONMENT VARIABLE:
 *   VITE_APP_FIREBASE_BACKEND_URL - Your backend base URL (e.g., https://excalidraw-collab.rckt.cloud)
 *
 * HOW TO MAINTAIN AFTER UPSTREAM SYNC:
 *   See CUSTOMIZATIONS.md in the project root
 */

const BACKEND_URL = import.meta.env.VITE_APP_FIREBASE_BACKEND_URL || '';

// =============================================================================
// Utility functions
// =============================================================================

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// =============================================================================
// firebase/app
// =============================================================================

export const initializeApp = (config: any) => {
  if (!BACKEND_URL) {
    console.warn('[Firebase Backend Adapter] VITE_APP_FIREBASE_BACKEND_URL not set');
  }
  return { name: '[BACKEND_ADAPTER]', options: config };
};

export const getApp = () => ({ name: '[BACKEND_ADAPTER]', options: {} });
export const getApps = () => [];
export const deleteApp = async () => {};

// =============================================================================
// firebase/firestore
// =============================================================================

interface DocRef {
  id: string;
  path: string;
  _collection: string;
}

interface DocSnapshot {
  _exists: boolean;
  _data: any;
  exists: () => boolean;
  data: () => any;
  id: string;
  ref: DocRef;
}

// Store for document references
const docRefStore = new Map<string, DocRef>();

export const getFirestore = () => ({ type: 'backend-adapter-firestore' });

export const doc = (_firestore: any, collection: string, id: string): DocRef => {
  const ref: DocRef = {
    id,
    path: `${collection}/${id}`,
    _collection: collection,
  };
  docRefStore.set(ref.path, ref);
  return ref;
};

export const getDoc = async (ref: DocRef): Promise<DocSnapshot> => {
  if (!BACKEND_URL) {
    return {
      _exists: false,
      _data: null,
      exists: () => false,
      data: () => null,
      id: ref.id,
      ref,
    };
  }

  try {
    const response = await fetch(`${BACKEND_URL}/api/${ref._collection}/${ref.id}`);

    if (response.status === 404) {
      return {
        _exists: false,
        _data: null,
        exists: () => false,
        data: () => null,
        id: ref.id,
        ref,
      };
    }

    if (!response.ok) {
      throw new Error(`Failed to get doc: ${response.status}`);
    }

    const json = await response.json();

    // Convert base64 strings back to Bytes objects
    const data = {
      sceneVersion: json.sceneVersion,
      iv: Bytes.fromBase64String(json.iv),
      ciphertext: Bytes.fromBase64String(json.ciphertext),
    };

    return {
      _exists: true,
      _data: data,
      exists: () => true,
      data: () => data,
      id: ref.id,
      ref,
    };
  } catch (error) {
    console.error('[Firebase Backend Adapter] getDoc error:', error);
    return {
      _exists: false,
      _data: null,
      exists: () => false,
      data: () => null,
      id: ref.id,
      ref,
    };
  }
};

export const setDoc = async (ref: DocRef, data: any): Promise<void> => {
  if (!BACKEND_URL) {
    console.warn('[Firebase Backend Adapter] Cannot setDoc: BACKEND_URL not configured');
    return;
  }

  try {
    const payload = {
      sceneVersion: data.sceneVersion,
      iv: data.iv instanceof Bytes ? data.iv.toBase64() : uint8ArrayToBase64(data.iv),
      ciphertext: data.ciphertext instanceof Bytes ? data.ciphertext.toBase64() : uint8ArrayToBase64(new Uint8Array(data.ciphertext)),
    };

    const response = await fetch(`${BACKEND_URL}/api/${ref._collection}/${ref.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to setDoc: ${response.status}`);
    }
  } catch (error) {
    console.error('[Firebase Backend Adapter] setDoc error:', error);
    throw error;
  }
};

export const updateDoc = async (ref: DocRef, data: any): Promise<void> => {
  // For our purposes, update is the same as set
  return setDoc(ref, data);
};

export const deleteDoc = async (ref: DocRef): Promise<void> => {
  if (!BACKEND_URL) return;

  try {
    await fetch(`${BACKEND_URL}/api/${ref._collection}/${ref.id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('[Firebase Backend Adapter] deleteDoc error:', error);
  }
};

// Collections (not used by Excalidraw collab, but stub for completeness)
export const collection = () => ({});
export const query = () => ({});
export const where = () => ({});
export const getDocs = async () => ({ docs: [], empty: true, size: 0 });
export const onSnapshot = () => () => {};

// Transaction support - this is the main way Excalidraw saves collaboration data
export const runTransaction = async (
  _firestore: any,
  updateFn: (transaction: any) => Promise<any>
): Promise<any> => {
  // Create a transaction object that tracks operations
  const operations: Array<{ type: 'set' | 'update' | 'delete'; ref: DocRef; data?: any }> = [];
  let getResult: DocSnapshot | null = null;

  const transaction = {
    get: async (ref: DocRef): Promise<DocSnapshot> => {
      getResult = await getDoc(ref);
      return getResult;
    },
    set: (ref: DocRef, data: any) => {
      operations.push({ type: 'set', ref, data });
      return transaction;
    },
    update: (ref: DocRef, data: any) => {
      operations.push({ type: 'update', ref, data });
      return transaction;
    },
    delete: (ref: DocRef) => {
      operations.push({ type: 'delete', ref });
      return transaction;
    },
  };

  // Execute the update function to collect operations
  const result = await updateFn(transaction);

  // Execute all collected operations
  for (const op of operations) {
    if (op.type === 'set' || op.type === 'update') {
      await setDoc(op.ref, op.data);
    } else if (op.type === 'delete') {
      await deleteDoc(op.ref);
    }
  }

  return result;
};

// Bytes class for Firestore binary data
export class Bytes {
  private data: Uint8Array;

  constructor(data: Uint8Array) {
    this.data = data;
  }

  toUint8Array(): Uint8Array {
    return this.data;
  }

  toBase64(): string {
    return uint8ArrayToBase64(this.data);
  }

  static fromUint8Array(array: Uint8Array): Bytes {
    return new Bytes(array);
  }

  static fromBase64String(base64: string): Bytes {
    return new Bytes(base64ToUint8Array(base64));
  }
}

// =============================================================================
// firebase/storage
// =============================================================================

interface StorageRef {
  fullPath: string;
  name: string;
  bucket: string;
}

export const getStorage = () => ({ type: 'backend-adapter-storage' });

// This is called by the original firebase.ts before uploading files
export const loadFirebaseStorage = async () => {
  console.log('[Firebase Backend Adapter] loadFirebaseStorage called');
  return getStorage();
};

export const ref = (_storage: any, path?: string): StorageRef => {
  console.log('[Firebase Backend Adapter] ref called with path:', path);
  // Normalize path: remove leading slash and 'files/' prefix if present
  // The caller passes paths like '/files/rooms/...' or 'files/rooms/...'
  // We want to store just 'rooms/...' since we'll add '/api/files/' later
  let normalizedPath = path || '';
  normalizedPath = normalizedPath.replace(/^\/+/, ''); // Remove leading slashes
  normalizedPath = normalizedPath.replace(/^files\//, ''); // Remove 'files/' prefix

  return {
    fullPath: normalizedPath,
    name: normalizedPath.split('/').pop() || '',
    bucket: 'backend-storage',
  };
};

export const uploadBytes = async (
  storageRef: StorageRef,
  data: Uint8Array | ArrayBuffer,
  metadata?: { cacheControl?: string }
): Promise<{ metadata: any; ref: StorageRef }> => {
  const url = `${BACKEND_URL}/api/files/${storageRef.fullPath}`;
  console.log('[Firebase Backend Adapter] uploadBytes called:', {
    url,
    fullPath: storageRef.fullPath,
    dataSize: data.byteLength,
    metadata,
  });

  if (!BACKEND_URL) {
    console.warn('[Firebase Backend Adapter] Cannot uploadBytes: BACKEND_URL not configured');
    return { metadata: {}, ref: storageRef };
  }

  try {
    const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
    const headers: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
    };
    // Note: Cache-Control header removed - was causing CORS issues
    // Backend can set caching policy on responses instead

    console.log('[Firebase Backend Adapter] Making PUT request to:', url);
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: bytes,
    });

    console.log('[Firebase Backend Adapter] PUT response:', response.status, response.statusText);

    if (!response.ok) {
      throw new Error(`Failed to upload: ${response.status}`);
    }

    return {
      metadata: { fullPath: storageRef.fullPath, name: storageRef.name },
      ref: storageRef,
    };
  } catch (error) {
    console.error('[Firebase Backend Adapter] uploadBytes error:', error);
    throw error;
  }
};

export const uploadString = async (
  storageRef: StorageRef,
  data: string,
  format?: string
): Promise<{ metadata: any; ref: StorageRef }> => {
  let bytes: Uint8Array;
  if (format === 'base64') {
    bytes = base64ToUint8Array(data);
  } else {
    bytes = new TextEncoder().encode(data);
  }
  return uploadBytes(storageRef, bytes);
};

export const getDownloadURL = async (storageRef: StorageRef): Promise<string> => {
  // Return the direct URL to the file
  return `${BACKEND_URL}/api/files/${storageRef.fullPath}`;
};

export const deleteObject = async (storageRef: StorageRef): Promise<void> => {
  if (!BACKEND_URL) return;

  try {
    await fetch(`${BACKEND_URL}/api/files/${storageRef.fullPath}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('[Firebase Backend Adapter] deleteObject error:', error);
  }
};

export const listAll = async () => ({ items: [], prefixes: [] });
export const getMetadata = async () => ({});
export const updateMetadata = async () => ({});

// =============================================================================
// firebase/auth (stub - not used by Excalidraw collab)
// =============================================================================

export const getAuth = () => ({ currentUser: null });
export const signInAnonymously = async () => ({ user: null });
export const signOut = async () => {};
export const onAuthStateChanged = (_auth: any, callback: any) => {
  callback(null);
  return () => {};
};
